import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import io, { Socket } from 'socket.io-client';
import { VAPI_CONFIG } from '../config/vapi';
import { useAuth } from './AuthContext';

export interface Call {
  id: string;
  date: string;
  time: string;
  duration: string;
  phoneNumber: string;
  transcript: string;
  audioUrl: string;
  status: 'active' | 'completed';
}

interface CallContextType {
  calls: Call[];
  activeCalls: Call[];
  metrics: {
    totalCalls: number;
    averageDuration: string;
    responseTime: string;
  };
  loading: boolean;
  error: string | null;
  refetchCalls: () => Promise<void>;
}

const CallContext = createContext<CallContextType | null>(null);

export const CallProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [calls, setCalls] = useState<Call[]>([]);
  const [activeCalls, setActiveCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState({
    totalCalls: 0,
    averageDuration: '0m 0s',
    responseTime: '0s',
  });

  const callsRef = useRef(calls);
  callsRef.current = calls;

  const updateMetrics = useCallback((allCalls: Call[]) => {
    const totalCalls = allCalls.length;
    const avgDuration = calculateAverageDuration(allCalls);
    const responseTime = calculateAverageResponseTime(allCalls);

    setMetrics({
      totalCalls,
      averageDuration: avgDuration,
      responseTime: responseTime,
    });
  }, []);

  const fetchCallHistory = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      setCalls([]);
      setActiveCalls([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${VAPI_CONFIG.API_ENDPOINT}/calls`, VAPI_CONFIG.API_OPTIONS);
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized. Please log in again.');
        }
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      
      const callsData = Array.isArray(data.calls) ? data.calls : [];
      const completedCalls = callsData.filter((call: Call) => call.status === 'completed');
      const currentActiveCalls = callsData.filter((call: Call) => call.status === 'active');
      
      setCalls(completedCalls);
      setActiveCalls(currentActiveCalls);
      updateMetrics([...completedCalls, ...currentActiveCalls]);
    } catch (error) {
      console.error('Error fetching call history:', error);
      setError(error instanceof Error ? error.message : 'Unable to connect to the server');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, updateMetrics]);

  const handleCallStarted = useCallback((call: Call) => {
    setActiveCalls(prev => [...prev, call]);
    updateMetrics([...callsRef.current, call]);
  }, [updateMetrics]);

  const handleCallEnded = useCallback((call: Call) => {
    setActiveCalls(prev => prev.filter(c => c.id !== call.id));
    setCalls(prev => [call, ...prev]);
    updateMetrics([...callsRef.current, call]);
  }, [updateMetrics]);

  const handleTranscriptUpdate = useCallback((data: { callId: string; transcript: string }) => {
    setActiveCalls(prev => prev.map(call => 
      call.id === data.callId ? { ...call, transcript: data.transcript } : call
    ));
  }, []);

  useEffect(() => {
    let currentSocket: Socket | null = null;
    let reconnectTimer: NodeJS.Timeout | null = null;

    const setupSocketConnection = () => {
      if (isAuthenticated && !currentSocket) {
        currentSocket = io(VAPI_CONFIG.WS_ENDPOINT, VAPI_CONFIG.SOCKET_OPTIONS);
        
        currentSocket.on('call:started', handleCallStarted);
        currentSocket.on('call:ended', handleCallEnded);
        currentSocket.on('call:transcript', handleTranscriptUpdate);

        currentSocket.on('connect', () => {
          console.log('Connected to WebSocket server');
          setError(null);
          fetchCallHistory();
        });

        currentSocket.on('connect_error', (err) => {
          console.error('Socket connection error:', err);
          setError('Connection to real-time updates failed. Retrying...');
          
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
          }
          
          reconnectTimer = setTimeout(() => {
            if (currentSocket) {
              currentSocket.connect();
            }
          }, 5000);
        });

        currentSocket.on('disconnect', () => {
          setError('Connection lost. Attempting to reconnect...');
        });

        setSocket(currentSocket);
      }
    };

    setupSocketConnection();

    return () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (currentSocket) {
        currentSocket.removeAllListeners();
        currentSocket.disconnect();
        setSocket(null);
      }
    };
  }, [isAuthenticated, handleCallStarted, handleCallEnded, handleTranscriptUpdate, fetchCallHistory]);

  const calculateAverageDuration = (calls: Call[]): string => {
    if (!calls || calls.length === 0) return '0m 0s';
    
    const completedCalls = calls.filter(call => call.status === 'completed');
    if (completedCalls.length === 0) return '0m 0s';
    
    const durations = completedCalls.map(call => {
      const [minutes, seconds] = (call.duration || '0:00').split(':').map(Number);
      return minutes * 60 + seconds;
    });
    
    const avgSeconds = Math.round(durations.reduce((a, b) => a + b, 0) / completedCalls.length);
    const minutes = Math.floor(avgSeconds / 60);
    const seconds = avgSeconds % 60;
    
    return `${minutes}m ${seconds}s`;
  };

  const calculateAverageResponseTime = (calls: Call[]): string => {
    return '5s';
  };

  const contextValue = {
    calls,
    activeCalls,
    metrics,
    loading,
    error,
    refetchCalls: fetchCallHistory
  };

  return (
    <CallContext.Provider value={contextValue}>
      {children}
    </CallContext.Provider>
  );
};

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};